import { useCallback, useEffect, useState } from 'react';
import { AgeGateOverlay } from '@hypneuronerd/age-gate';
import { storage } from '@hypneuronerd/storage';
import { ReturnLink } from './components/ReturnLink';
import { ScenarioList } from './components/ScenarioList';
import { Playthrough } from './components/Playthrough';
import { ReflectionView } from './components/ReflectionView';
import { SCENARIOS, getScenario } from './data/scenarios';
import type { Choice } from './data/types';
import styles from './App.module.css';

type Phase =
  | { kind: 'list' }
  | { kind: 'play'; scenarioId: string; nodeId: string; path: string[] }
  | { kind: 'reflect'; scenarioId: string; path: string[] };

const PROGRESS_KEY = 'negotiation-sim:progress';
const COMPLETED_KEY = 'negotiation-sim:completed';

type Progress = Record<string, { nodeId: string }>;
type Completed = Record<string, boolean>;

export function App() {
  const [phase, setPhase] = useState<Phase>({ kind: 'list' });
  const [progress, setProgress] = useState<Progress>(() => storage.get(PROGRESS_KEY, {}));
  const [completed, setCompleted] = useState<Completed>(() => storage.get(COMPLETED_KEY, {}));

  useEffect(() => storage.set(PROGRESS_KEY, progress), [progress]);
  useEffect(() => storage.set(COMPLETED_KEY, completed), [completed]);

  function play(id: string) {
    const scenario = getScenario(id);
    if (!scenario) return;
    const savedNode = progress[id]?.nodeId;
    const nodeId = savedNode && scenario.nodes[savedNode] ? savedNode : scenario.startNode;
    // Resuming a saved playthrough loses the in-flight path history
    // (we don't persist it). The reflection still works — it shows the
    // path from wherever the visitor resumed.
    setPhase({ kind: 'play', scenarioId: id, nodeId, path: [] });
  }

  const handleChoice = useCallback(
    (choice: Choice) => {
      if (phase.kind !== 'play') return;
      const nextNode = choice.nextNode;
      setProgress((p) => ({ ...p, [phase.scenarioId]: { nodeId: nextNode } }));
      // Append the chosen line to the path trail so the reflection
      // view can render the visitor's actual choices alongside the
      // scenario's "other paths" descriptions.
      setPhase({
        kind: 'play',
        scenarioId: phase.scenarioId,
        nodeId: nextNode,
        path: [...phase.path, choice.text],
      });
    },
    [phase]
  );

  const handleComplete = useCallback(() => {
    if (phase.kind !== 'play') return;
    setCompleted((c) => ({ ...c, [phase.scenarioId]: true }));
    setProgress((p) => {
      const { [phase.scenarioId]: _drop, ...rest } = p;
      return rest;
    });
    setPhase({ kind: 'reflect', scenarioId: phase.scenarioId, path: phase.path });
  }, [phase]);

  const handleExit = useCallback(() => {
    setPhase({ kind: 'list' });
  }, []);

  const inProgress: Record<string, boolean> = Object.fromEntries(
    Object.keys(progress).map((k) => [k, true])
  );

  return (
    <>
      <AgeGateOverlay />
      <header className={styles.topNav}>
        <ReturnLink />
        {phase.kind !== 'list' && (
          <button type="button" className={styles.backBtn} onClick={handleExit}>
            ← scenarios
          </button>
        )}
      </header>

      <main className={styles.stage}>
        {phase.kind === 'list' && (
          <ScenarioList
            scenarios={SCENARIOS}
            inProgress={inProgress}
            completed={completed}
            onPlay={play}
          />
        )}

        {phase.kind === 'play' && (() => {
          const scenario = getScenario(phase.scenarioId);
          if (!scenario) return null;
          return (
            <Playthrough
              scenario={scenario}
              currentNodeId={phase.nodeId}
              onChoice={handleChoice}
              onComplete={handleComplete}
              onExit={handleExit}
            />
          );
        })()}

        {phase.kind === 'reflect' && (() => {
          const scenario = getScenario(phase.scenarioId);
          if (!scenario) return null;
          return (
            <ReflectionView
              reflection={scenario.reflection}
              path={phase.path}
              onReplay={() => {
                setProgress((p) => {
                  const { [scenario.id]: _drop, ...rest } = p;
                  return rest;
                });
                play(scenario.id);
              }}
              onList={handleExit}
            />
          );
        })()}
      </main>

      <footer className={styles.footer}>
        <p>
          there is no correct path. each choice opens different doors. read
          like fiction; reflect like it might be true.
        </p>
      </footer>
    </>
  );
}
